/**
 * Client binding for `generateAiEstimation` + hook.
 *
 * Same lifecycle as `useAiSynthese` / `useAiRedFlags` :
 *   - Subscribe to `/users/{uid}/fiches/{id}/ai/estimation` for live picks-up.
 *   - Fire the callable on mount; server decides cache-hit vs regen via
 *     `inputsHash`.
 *
 * Unlike the other two IA blocks, this one can return a "ready" state with
 * `unavailableReason` set — that's a legitimate factual answer ("pas assez
 * de comparables"), NOT an error. The UI renders it as a neutral message
 * instead of the figure.
 */

import { useEffect, useState } from "react";
import { doc, onSnapshot, type DocumentData } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../firebase";

// ─── Doc shape (keep in sync with functions/src/generateAiEstimation.ts) ─
export type AiEstimationModifierKey =
  | "dpe"
  | "ecoles"
  | "commerces"
  | "transports"
  | "fibre";

export type AiEstimationModifier = {
  key: AiEstimationModifierKey;
  label: string;
  pct: number; // already capped server-side to [-10, +10]
  rationale: string;
};

export type AiEstimation = {
  version: 1;
  propertyType: "Maison" | "Appartement" | "Immeuble" | "Terrain" | null;
  surfaceM2: number | null;
  surfaceBasis: "bati" | "terrain" | null;
  dvf: {
    medianPricePerM2: number | null;
    basis: "bati" | "terrain" | null;
    comparableCount: number;
    radiusMeters: number;
    sinceYear: number;
  };
  basePriceEur: number | null;
  modifiers: AiEstimationModifier[];
  adjustedPriceEur: number | null;
  low: number | null;
  mid: number | null;
  high: number | null;
  rationale: string;
  unavailableReason: string | null;
  generatedAt: string;
  model: string;
  inputsHash: string;
  pdfInclude: boolean;
};

const callGenerate = httpsCallable<{ ficheId: string }, AiEstimation>(
  functions,
  "generateAiEstimation",
);

// ─── Hook ────────────────────────────────────────────────────────────────
type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; data: AiEstimation; fromServer: boolean }
  | { status: "error"; message: string };

export function useAiEstimation(ficheId: string | null): State {
  const [state, setState] = useState<State>({ status: "idle" });

  useEffect(() => {
    if (!ficheId) {
      setState({ status: "idle" });
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    const ref = doc(
      db,
      "users",
      user.uid,
      "fiches",
      ficheId,
      "ai",
      "estimation",
    );
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (cancelled) return;
        if (!snap.exists()) return;
        const parsed = parseAiEstimation(snap.data());
        if (parsed) {
          setState({ status: "ready", data: parsed, fromServer: false });
        }
      },
      (err) => {
        console.warn("[ai/estimation] snapshot error", err);
      },
    );

    callGenerate({ ficheId })
      .then((res) => {
        if (cancelled) return;
        setState({
          status: "ready",
          data: res.data as AiEstimation,
          fromServer: true,
        });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState((prev) =>
          prev.status === "ready"
            ? prev
            : { status: "error", message: extractMsg(e) },
        );
      });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [ficheId]);

  return state;
}

// ─── Helpers ─────────────────────────────────────────────────────────────
function parseAiEstimation(raw: DocumentData): AiEstimation | null {
  if (!raw || raw.version !== 1) return null;

  const dvfRaw = (raw.dvf ?? {}) as Record<string, unknown>;
  const dvf = {
    medianPricePerM2: num(dvfRaw.medianPricePerM2),
    basis:
      dvfRaw.basis === "bati" || dvfRaw.basis === "terrain"
        ? (dvfRaw.basis as "bati" | "terrain")
        : null,
    comparableCount: typeof dvfRaw.comparableCount === "number" ? dvfRaw.comparableCount : 0,
    radiusMeters: typeof dvfRaw.radiusMeters === "number" ? dvfRaw.radiusMeters : 500,
    sinceYear:
      typeof dvfRaw.sinceYear === "number"
        ? dvfRaw.sinceYear
        : new Date().getFullYear() - 5,
  };

  const modifiersRaw = Array.isArray(raw.modifiers) ? raw.modifiers : [];
  const modifiers: AiEstimationModifier[] = [];
  for (const m of modifiersRaw) {
    if (!m || typeof m !== "object") continue;
    const key = (m as Record<string, unknown>).key;
    if (
      key !== "dpe" &&
      key !== "ecoles" &&
      key !== "commerces" &&
      key !== "transports" &&
      key !== "fibre"
    )
      continue;
    const label = str((m as Record<string, unknown>).label);
    const pct = (m as Record<string, unknown>).pct;
    const rationale = str((m as Record<string, unknown>).rationale);
    if (!label || typeof pct !== "number" || !Number.isFinite(pct) || !rationale)
      continue;
    modifiers.push({ key, label, pct, rationale });
  }

  const propertyType = raw.propertyType;
  const typedPropertyType:
    | "Maison"
    | "Appartement"
    | "Immeuble"
    | "Terrain"
    | null =
    propertyType === "Maison" ||
    propertyType === "Appartement" ||
    propertyType === "Immeuble" ||
    propertyType === "Terrain"
      ? propertyType
      : null;

  const surfaceBasis =
    raw.surfaceBasis === "bati" || raw.surfaceBasis === "terrain"
      ? (raw.surfaceBasis as "bati" | "terrain")
      : null;

  return {
    version: 1,
    propertyType: typedPropertyType,
    surfaceM2: num(raw.surfaceM2),
    surfaceBasis,
    dvf,
    basePriceEur: num(raw.basePriceEur),
    modifiers,
    adjustedPriceEur: num(raw.adjustedPriceEur),
    low: num(raw.low),
    mid: num(raw.mid),
    high: num(raw.high),
    rationale: typeof raw.rationale === "string" ? raw.rationale : "",
    unavailableReason:
      typeof raw.unavailableReason === "string" && raw.unavailableReason.length > 0
        ? raw.unavailableReason
        : null,
    generatedAt: typeof raw.generatedAt === "string" ? raw.generatedAt : "",
    model: typeof raw.model === "string" ? raw.model : "",
    inputsHash: typeof raw.inputsHash === "string" ? raw.inputsHash : "",
    pdfInclude: Boolean(raw.pdfInclude),
  };
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function str(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}
function extractMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "object" && e && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return String(e);
}
